import Pusher from 'pusher-js';

const PUSHER_APP_KEY = process.env.REACT_APP_PUSHER_APP_KEY || 'YOUR_APP_KEY';
const PUSHER_APP_CLUSTER = process.env.REACT_APP_PUSHER_APP_CLUSTER || 'YOUR_APP_CLUSTER';

/**
 * Service for managing Pusher client instance and connections.
 * Implements the Singleton pattern to ensure only one connection exists.
 */
export class PusherService {
  static #instance = null;

  /**
   * @type {Pusher|null}
   * @private
   */
  #pusherClient = null;

  /**
   * @type {boolean}
   * @private
   */
  #isConnected = false;

  /**
   * @type {Error|null}
   * @private
   */
  #connectionError = null;

  constructor() {
    if (PusherService.#instance) {
      return PusherService.#instance;
    }
    PusherService.#instance = this;
  }

  /**
   * Get the singleton instance of PusherService
   * @returns {PusherService}
   */
  static getInstance() {
    if (!PusherService.#instance) {
      PusherService.#instance = new PusherService();
    }
    return PusherService.#instance;
  }

  /**
   * Initialize and get the Pusher client
   * @returns {Pusher|null}
   */
  initialize() {
    if (this.#pusherClient) {
      return this.#pusherClient;
    }

    if (!PUSHER_APP_KEY || PUSHER_APP_KEY === "YOUR_APP_KEY") {
      this.#setConnectionError(new Error("Pusher App Key is not configured. Please set REACT_APP_PUSHER_APP_KEY."));
      return null;
    }

    if (!PUSHER_APP_CLUSTER || PUSHER_APP_CLUSTER === "YOUR_APP_CLUSTER") {
      this.#setConnectionError(new Error("Pusher App Cluster is not configured. Please set REACT_APP_PUSHER_APP_CLUSTER."));
      return null;
    }

    try {
      this.#pusherClient = new Pusher(PUSHER_APP_KEY, {
        cluster: PUSHER_APP_CLUSTER,
      });

      this.#setupConnectionHandlers();
    } catch (error) {
      this.#setConnectionError(error);
      return null;
    }

    return this.#pusherClient;
  }

  /**
   * Set up connection event handlers
   * @private
   */
  #setupConnectionHandlers() {
    if (!this.#pusherClient) return;

    this.#pusherClient.connection.bind("connected", () => {
      console.log("Pusher connected successfully!");
      this.#isConnected = true;
      this.#connectionError = null;
    });

    this.#pusherClient.connection.bind("disconnected", () => {
      console.log("Pusher disconnected");
      this.#isConnected = false;
    });

    this.#pusherClient.connection.bind("error", (err) => {
      this.#setConnectionError(err);
    });
  }

  /**
   * Set connection error and update state
   * @private
   * @param {Error} error
   */
  #setConnectionError(error) {
    this.#connectionError = error;
    this.#isConnected = false;
    console.error("Pusher error:", error?.message || error);
  }

  /**
   * Get the Pusher client instance
   * @returns {Pusher|null}
   */
  getClient() {
    if (!this.#pusherClient) {
      return this.initialize();
    }
    return this.#pusherClient;
  }

  /**
   * Get the current Pusher socket ID if connected
   * @returns {string|null}
   */
  getSocketId() {
    return this.#pusherClient?.connection?.socket_id || null;
  }

  /**
   * Check if Pusher is currently connected
   * @returns {boolean}
   */
  isConnected() {
    return this.#isConnected;
  }

  /**
   * Get the current connection error if any
   * @returns {Error|null}
   */
  getConnectionError() {
    return this.#connectionError;
  }

  /**
   * Disconnect Pusher client if connected
   */
  disconnect() {
    if (this.#pusherClient) {
      this.#pusherClient.disconnect();
      this.#isConnected = false;
    }
  }
}

// Export the singleton instance as the primary export
export const pusherService = PusherService.getInstance();